import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type ViewProps } from 'react-native';
import { type SharedValue } from 'react-native-reanimated';
import { CustomNavigationHeader, useResponsive } from '@sg/garnish';

import { CustomizationNavigationHeader } from '@order/features/customization';

import { useCustomizationNavigation } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationHeader = (props: Props) => {
  const {
    scrollOffsetSV,
    text,
    restaurantSlug,
    addressId,
    spacing,
    shouldDisableControls,
    isShowingText = true,
    shouldAnimateHeaderContainer = false,
    shouldAnimateHeaderText = false,
    style,
    onBackButtonPress,
  } = props;

  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  const { navigateToPreviousScreen } = useCustomizationNavigation({
    restaurantSlug,
    addressId,
  });

  const shouldRenderText = Boolean(isShowingText && text);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationNavigationHeader.Container
      style={style}
      spacing={spacing}
      scrollOffsetSV={scrollOffsetSV}
      buttonsReservedSpace={currentBreakpoint.isXS ? 'left-and-right' : 'left'}
      transition={shouldAnimateHeaderContainer ? 'background-and-border' : null}
    >
      <CustomizationNavigationHeader.Button.Left
        name="IconArrowLeft"
        onPress={onBackButtonPress ?? navigateToPreviousScreen}
        accessibilityLabel={formatMessage(messages.backBtnA11yLabel)}
        disabled={shouldDisableControls}
      />

      {shouldRenderText ? (
        <CustomNavigationHeader.Content
          scrollOffsetSV={scrollOffsetSV}
          transition={shouldAnimateHeaderText ? 'opacity' : null}
        >
          <CustomizationNavigationHeader.Text>
            {text}
          </CustomizationNavigationHeader.Text>
        </CustomNavigationHeader.Content>
      ) : null}

      {currentBreakpoint.isXS ? (
        <CustomizationNavigationHeader.Button.Right
          name="IconClose"
          onPress={navigateToPreviousScreen}
          accessibilityLabel={formatMessage(messages.closeBtnA11yLabel)}
          disabled={shouldDisableControls}
        />
      ) : null}
    </CustomizationNavigationHeader.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  backBtnA11yLabel: {
    defaultMessage: 'Go back',
    description: 'Customization | Navigation header | Back button a11y label',
  },
  closeBtnA11yLabel: {
    defaultMessage: 'Close',
    description: 'Customization | Navigation header | Close button a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  restaurantSlug: string | undefined;
  addressId: string | undefined;
  text?: string;
  isShowingText?: boolean;
  shouldAnimateHeaderContainer?: boolean;
  shouldAnimateHeaderText?: boolean;
  shouldDisableControls?: boolean;
  scrollOffsetSV: SharedValue<number>;
  spacing?: 'standard' | 'narrow';
  style?: ViewProps['style'];
  onBackButtonPress?: () => void;
};
